import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { VatStatusBadge, VatTabs } from '@/components/vat';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatTrn } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { negate, useVatReturns, useVatSettings, type VatReturnSummary } from '@/lib/vat';

export const Route = createFileRoute('/taxes/vat/')({ component: VatCentre });

const MONTHS = ['January', 'February', 'March'];

/** Screen T1 (document 06): Taxes › VAT, return cards per period, then the history. */
function VatCentre() {
  const orgId = useOrgId();
  const settings = useVatSettings();
  const returns = useVatReturns();
  const s = settings.data;
  const list = returns.data ?? [];
  const active = list.filter((r) => r.status !== 'paid');
  const history = list.filter((r) => r.status === 'paid');

  return (
    <>
      <PageHeader title="VAT" description="VAT returns for each tax period, from the books." />
      <VatTabs />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : settings.isPending || returns.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : settings.isError || returns.isError ? (
        <EmptyState
          title="Could not load VAT returns"
          hint={(settings.error ?? returns.error)?.message}
        />
      ) : !s?.vatRegistered && list.length === 0 ? (
        <EmptyState
          title="This client is not registered for VAT"
          hint="Add the TRN and registration date in Company settings to start preparing VAT returns."
          action={
            <Button asChild variant="outline">
              <Link to="/settings/company">Company settings</Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-x-8 gap-y-1 rounded-lg border bg-card px-4 py-3 text-sm">
            <span>
              <span className="text-muted-foreground">TRN </span>
              <span className="tnum font-medium">{formatTrn(s?.trn ?? null) || '—'}</span>
            </span>
            <span>
              <span className="text-muted-foreground">Filing frequency </span>
              <span className="font-medium">
                {s?.frequency === 'monthly'
                  ? 'Monthly'
                  : `Quarterly, first quarter starting in ${MONTHS[(s?.quarterStartMonth ?? 1) - 1]}`}
              </span>
            </span>
            {s?.registrationDate ? (
              <span>
                <span className="text-muted-foreground">Registered since </span>
                <span className="font-medium">{displayDate(s.registrationDate)}</span>
              </span>
            ) : null}
          </div>
          {active.length ? (
            <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {active.map((r) => (
                <ReturnCard key={r.key} r={r} currency={s?.currency ?? 'AED'} />
              ))}
            </div>
          ) : null}
          <h2 className="mb-2 text-base font-semibold">Filed and paid</h2>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No paid returns yet.</p>
          ) : (
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead className="w-[120px]">Filed</TableHead>
                    <TableHead className="w-[120px]">Due</TableHead>
                    <TableHead className="w-[150px] text-right">Net VAT</TableHead>
                    <TableHead className="w-[90px]">Status</TableHead>
                    <TableHead className="w-[120px] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((r) => (
                    <TableRow key={r.key}>
                      <TableCell>
                        <div className="font-medium">{r.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {displayDate(r.periodStart)} – {displayDate(r.periodEnd)}
                        </div>
                      </TableCell>
                      <TableCell>{r.filedOn ? displayDate(r.filedOn) : ''}</TableCell>
                      <TableCell>{displayDate(r.dueDate)}</TableCell>
                      <TableCell className="tnum text-right">
                        {s?.currency} {formatAmount(r.netVat)}
                      </TableCell>
                      <TableCell>
                        <VatStatusBadge r={r} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild size="sm" variant="link" className="px-0">
                          <Link to="/taxes/vat/returns/$key" params={{ key: r.key }}>
                            View return
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </>
  );
}

function ReturnCard({ r, currency }: { r: VatReturnSummary; currency: string }) {
  const refund = r.netVat.startsWith('-');
  const action =
    r.status === 'filed'
      ? refund
        ? 'Record refund'
        : 'Record payment'
      : r.status === 'open'
        ? 'View running totals'
        : 'Prepare return';
  return (
    <section className="flex flex-col rounded-lg border bg-card p-4" data-testid="vat-card">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">{r.label}</h2>
          <p className="text-xs text-muted-foreground">
            {displayDate(r.periodStart)} – {displayDate(r.periodEnd)} · Due {displayDate(r.dueDate)}
          </p>
        </div>
        <VatStatusBadge r={r} />
      </div>
      <dl className="mb-3 grid grid-cols-2 gap-y-1 text-sm">
        <dt className="text-muted-foreground">VAT on sales</dt>
        <dd className="tnum text-right">{formatAmount(r.outputVat)}</dd>
        <dt className="text-muted-foreground">VAT on purchases</dt>
        <dd className="tnum text-right">{formatAmount(r.inputVat)}</dd>
        <dt className="font-medium">{refund ? 'Net VAT refundable' : 'Net VAT payable'}</dt>
        <dd className="tnum text-right font-semibold">
          {currency} {formatAmount(refund ? negate(r.netVat) : r.netVat)}
        </dd>
      </dl>
      {r.status === 'open' ? (
        <p className="mb-3 text-xs text-muted-foreground">Running totals for the period so far.</p>
      ) : null}
      {r.exceptions ? (
        <p className="mb-3 inline-flex items-center gap-1.5 text-xs text-[#B45309]">
          <AlertTriangle className="size-3.5" aria-hidden />
          {r.exceptions} exception{r.exceptions === 1 ? '' : 's'} to review
        </p>
      ) : null}
      <div className="mt-auto">
        <Button asChild size="sm" variant={r.status === 'open' ? 'outline' : 'default'}>
          <Link to="/taxes/vat/returns/$key" params={{ key: r.key }}>
            {action}
          </Link>
        </Button>
      </div>
    </section>
  );
}
